/**
 * HopEx brand marks rendered from the traced vector geometry (BRAND_ASSET_REGISTER §3–5).
 * Colours follow the theme: emblem and "HOP" use the current text colour, "EX" uses the
 * theme's accessible gold (`--hx-accent-text`: #F0B90B on dark, #7A5A12 on light).
 */
import { BRAND_GEOMETRY as G } from './geometry.ts';

interface MarkProps {
  /** Rendered height in px; width follows the aspect ratio. */
  size?: number;
  className?: string;
  /** Decorative marks are hidden from assistive tech; labelled marks announce "HopEx". */
  decorative?: boolean;
}

function a11y(decorative: boolean | undefined) {
  return decorative
    ? { 'aria-hidden': true as const }
    : { role: 'img' as const, 'aria-label': 'HopEx' };
}

export function HopexEmblem({ size = 32, className, decorative }: MarkProps) {
  const e = G.emblem;
  return (
    <svg
      viewBox={`0 0 ${e.width} ${G.height}`}
      height={size}
      width={(size * e.width) / G.height}
      className={className}
      {...a11y(decorative)}
    >
      <path fill="currentColor" fillRule="evenodd" transform={`translate(${e.x} ${e.y})`} d={e.d} />
    </svg>
  );
}

export function HopexLockup({ size = 28, className, decorative }: MarkProps) {
  const e = G.emblem;
  const w = G.wordmark;
  const wordX = e.width + G.lockupGap;
  const total = wordX + w.width;
  const letter = (k: 'H' | 'O' | 'P' | 'E' | 'X') => {
    const l = w.letters[k];
    return (
      <path key={k} fillRule="evenodd" transform={`translate(${wordX + l.x} ${l.y})`} d={l.d} />
    );
  };
  return (
    <svg
      viewBox={`0 0 ${total} ${G.height}`}
      height={size}
      width={(size * total) / G.height}
      className={className}
      direction="ltr"
      {...a11y(decorative)}
    >
      <g fill="currentColor">
        <path fillRule="evenodd" transform={`translate(${e.x} ${e.y})`} d={e.d} />
        {letter('H')}
        {letter('O')}
        {letter('P')}
      </g>
      <g fill="var(--hx-accent-text)">
        {letter('E')}
        {letter('X')}
      </g>
    </svg>
  );
}
